/**
 * Dashboard progress helpers.
 * Prefer hex / inline styles so colors work even when class names come from the API
 * (Tailwind cannot detect dynamic class strings at build time).
 */

export const DASHBOARD_BAR_HEX = {
  Open: '#3b82f6',
  'In Progress': '#f59e0b',
  Closed: '#10b981',
  Lost: '#ef4444',
  Won: '#10b981',
  LinkedIn: '#0ea5e9',
  Facebook: '#6366f1',
  Meta: '#6366f1',
  Offline: '#059669',
  Hot: '#ef4444',
  Warm: '#f59e0b',
  Cold: '#0ea5e9',
  Unqualified: '#94a3b8',
}

const FALLBACK_HEX = '#64748b'

/**
 * Resolve a solid fill color for dots / progress bars.
 */
export function getDashboardBarColor(item = {}) {
  if (item.hex) return item.hex
  if (item.name && DASHBOARD_BAR_HEX[item.name]) {
    return DASHBOARD_BAR_HEX[item.name]
  }
  return FALLBACK_HEX
}

/**
 * Width as a percentage string from count / total.
 * When count > 0, enforces a small minimum so thin bars stay visible.
 */
export function getDashboardBarWidth(count, total, { minVisible = 4 } = {}) {
  const c = Number(count) || 0
  const t = Number(total) || 0

  if (c <= 0 || t <= 0) return '0%'

  const pct = (c / t) * 100
  return `${Math.min(100, Math.max(pct, minVisible))}%`
}

/**
 * Prefer API percent/width when valid; otherwise derive from counts.
 */
export function resolveDashboardBarWidth(item, total) {
  if (typeof item?.percent === 'number' && Number.isFinite(item.percent)) {
    const value = item.percent
    if (value <= 0) return '0%'
    return `${Math.min(100, Math.max(value, 4))}%`
  }

  const raw = item?.width
  if (typeof raw === 'string' && raw.trim().endsWith('%')) {
    const value = Number.parseFloat(raw)
    if (Number.isFinite(value) && value > 0) {
      return `${Math.min(100, Math.max(value, 4))}%`
    }
    if (value === 0) return '0%'
  }

  if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) {
    return `${Math.min(100, Math.max(raw, 4))}%`
  }

  return getDashboardBarWidth(item?.count, total ?? item?.total)
}
